import React, { useMemo } from 'react';
import { Trash2, Copy } from 'lucide-react';
import IndicatorSelector from './IndicatorSelector';
import { Input } from '../../ui/input';
import { useIndicatorMetadata } from '../../../context/IndicatorMetadataContext';
import { Select } from '../../ui/select';
import { TIMEFRAMES, isTfLower, isCleanMultiple } from '../../../lib/timeframes';
import WindowPredicateBuilder from './WindowPredicateBuilder';
import MathNode from './MathNode';

const SegmentedTiny = ({ value, onChange, options }) => (
  <div className="inline-flex items-center p-0.5 bg-muted rounded-md">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
  className={`px-2 py-0.5 text-xs rounded ${value === o.value ? 'bg-backgroundSurface text-textPrimary shadow-sm' : 'text-textSecondary'}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const IndicatorCard = ({ value, onChange, className = '', baseTimeframe }) => (
  <div className={`rounded-lg border border-borderDefault bg-backgroundSurface/50 p-3 space-y-2 max-w-[75%] mx-auto ${className}`}>
  <IndicatorSelector dense value={value} onChange={onChange} baseTimeframe={baseTimeframe} />
  </div>
);

const Clause = ({ clause, onUpdate, onRemove, onDuplicate, baseTimeframe = 'M1' }) => {
  const normalized = clause?.type === 'clause' ? clause : { type: 'clause', ...(clause || {}) };
  const { operatorMetadata } = useIndicatorMetadata();

  const detectOperandKind = (opnd) => {
    if (opnd && typeof opnd === 'object') {
      if (opnd.kind === 'window_pred') return 'window';
      if (opnd.op && opnd.lhs !== undefined && opnd.rhs !== undefined) return 'math';
      if (opnd.type === 'indicator') return 'indicator';
    }
    return 'number';
  };
  const lhsKind = useMemo(() => detectOperandKind(normalized.lhs), [normalized.lhs]);
  const rhsKind = useMemo(() => detectOperandKind(normalized.rhs), [normalized.rhs]);
  const isLhsWindow = lhsKind === 'window';
  const lhsWindowOp = isLhsWindow ? (normalized?.lhs?.op || 'ANY_TRUE_LAST_N') : null;
  const lhsIsBooleanWindow = isLhsWindow && lhsWindowOp !== 'COUNT_TRUE_LAST_N';

  const setLhsType = (val) => {
    onUpdate((prev) => {
      const base = prev?.type === 'clause' ? prev : normalized;
      if (val === 'window') {
  // default RHS to boolean true and operator equals for a simple window boolean
  return { ...base, lhs: { kind:'window_pred', op:'ANY_TRUE_LAST_N', inputRef:'', n:3 }, op: 'equals', rhs: 1 };
      }
      if (val === 'math') {
        return {
          ...base,
          lhs: base?.lhs && base.lhs.op && base.lhs.lhs !== undefined ? base.lhs : { op:'SUB', lhs:{ type:'price', source:'close' }, rhs: 0 },
        };
      }
      // default to indicator
      const prevInd = (typeof base.lhs === 'object' && base.lhs?.type === 'indicator') ? base.lhs : null;
      return {
        ...base,
        lhs: prevInd || { type: 'indicator', name: 'ema', params: { length: 21 }, input: { type: 'price', source: 'close' } },
      };
    });
  };

  const setRhsType = (val) => {
    onUpdate((prev) => {
      const base = prev?.type === 'clause' ? prev : normalized;
      if (import.meta.env?.DEV) {
        console.log('[Clause] setRhsType', { from: (typeof base.rhs === 'object' ? 'indicator' : 'number'), to: val, before: base });
      }
      if (val === 'indicator') {
        const prevInd = (typeof base.rhs === 'object' && base.rhs?.type === 'indicator') ? base.rhs : null;
        const next = {
          ...base,
          rhs: prevInd || { type: 'indicator', name: 'ema', params: { length: 50 }, input: { type: 'price', source: 'close' } },
        };
        if (import.meta.env?.DEV) console.log('[Clause] setRhsType result', next);
        return next;
      }
      if (val === 'math') {
        const next = { ...base, rhs: (base?.rhs && base.rhs.op && base.rhs.lhs !== undefined) ? base.rhs : { op:'SUB', lhs:{ type:'price', source:'close' }, rhs: 0 } };
        if (import.meta.env?.DEV) console.log('[Clause] setRhsType result', next);
        return next;
      }
      if (val === 'window') {
        const next = { ...base, rhs: { kind:'window_pred', op:'ANY_TRUE_LAST_N', inputRef:'', n:3 } };
        if (import.meta.env?.DEV) console.log('[Clause] setRhsType result', next);
        return next;
      }
      const next = { ...base, rhs: 0 };
      if (import.meta.env?.DEV) console.log('[Clause] setRhsType result', next);
      return next;
    });
  };

  const defaultOps = ['crosses_above', 'crosses_below', 'greater_than', 'less_than', 'equals', 'not_equals'];
  const compactOpList = operatorMetadata?.operators || defaultOps;
  const numericOps = ['greater_than', 'greater_than_or_equal', 'less_than', 'less_than_or_equal', 'equals', 'not_equals'];

  return (
    <div className="flex items-center gap-3">
      {/* LHS */}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <div />
          <SegmentedTiny
            value={lhsKind}
            onChange={setLhsType}
            options={[
              { label: 'Indicator', value: 'indicator' },
              { label: 'Math', value: 'math' },
              { label: 'Window', value: 'window' },
            ]}
          />
        </div>
        {lhsKind === 'window' ? (
          <WindowPredicateBuilder
            value={normalized.lhs}
            onChange={(v)=>onUpdate((prev)=>({ ...(prev||normalized), lhs: v }))}
            baseTimeframe={baseTimeframe}
          />
        ) : lhsKind === 'math' ? (
          <MathNode
            value={normalized.lhs && typeof normalized.lhs === 'object' ? normalized.lhs : { op:'SUB', lhs:{ type:'price', source:'close' }, rhs:0 }}
            onChange={(next)=>onUpdate((prev)=>({ ...(prev||normalized), lhs: next }))}
            baseTimeframe={baseTimeframe}
          />
        ) : (
          <IndicatorCard
            value={
              (normalized.lhs && normalized.lhs.type === 'indicator') ? normalized.lhs : {
                type: 'indicator',
                name: 'ema',
                params: { length: 21 },
                input: { type: 'price', source: 'close' },
              }
            }
            onChange={(next) => onUpdate((prev) => {
              const base = prev || normalized;
              if (import.meta.env?.DEV) {
                console.log('[Clause] lhsChange', { beforeLen: base?.lhs?.params?.length, nextLen: next?.params?.length, before: base, next });
              }
              return { ...base, lhs: next };
            })}
            baseTimeframe={baseTimeframe}
          />
        )}
        {/* LHS TF override + chip */}
        <div className="mt-1 flex items-center gap-2">
          <select
            className="h-7 px-2 rounded border border-borderDefault bg-backgroundSurface text-textPrimary text-xs"
            title="Operand timeframe override"
            value={normalized?.lhs?.timeframe || ''}
            onChange={(e) => onUpdate((prev) => ({ ...(prev || normalized), lhs: { ...(prev?.lhs || normalized.lhs), timeframe: e.target.value || undefined } }))}
          >
            <option value="">Base ({normalized?.timeframe || baseTimeframe})</option>
            {TIMEFRAMES.map(tf => <option key={tf} value={tf}>{tf}</option>)}
          </select>
          {normalized?.lhs?.timeframe && (
            <GuardrailHint tf={normalized.lhs.timeframe} base={normalized?.timeframe || baseTimeframe} />
          )}
          {(normalized?.lhs?.timeframe && (normalized?.lhs?.timeframe !== (normalized?.timeframe || baseTimeframe))) && (
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-muted text-textSecondary" title="Operand timeframe override">
              TF: {normalized.lhs.timeframe}
            </span>
          )}
        </div>
      </div>

      {/* Operator */}
      <div className="flex flex-col items-center justify-center w-40">
        {lhsIsBooleanWindow ? (
          <Select value={'equals'} disabled className="w-full">
            <option value="equals">equals</option>
          </Select>
        ) : (
          <Select
            value={normalized.op || 'crosses_above'}
            onValueChange={(v) => onUpdate((prev) => {
              const base = prev || normalized;
              if (import.meta.env?.DEV) console.log('[Clause] opChange', { from: base.op, to: v, lhsLen: base?.lhs?.params?.length, rhsLen: base?.rhs?.params?.length });
              return { ...base, op: v };
            })}
            placeholder="Operator"
            className="w-full"
          >
            {(isLhsWindow && lhsWindowOp === 'COUNT_TRUE_LAST_N' ? numericOps : compactOpList).map((op) => (
              <option key={op} value={op}>
                {op.replace(/_/g, ' ')}
              </option>
            ))}
          </Select>
        )}
      </div>

      {/* RHS */}
      <div className="flex-1">
        {!isLhsWindow && (
          <div className="flex items-center justify-end mb-2">
            <SegmentedTiny
              value={rhsKind}
              onChange={setRhsType}
              options={[
                { label: 'Indicator', value: 'indicator' },
                { label: 'Math', value: 'math' },
                { label: 'Number', value: 'number' },
                { label: 'Window', value: 'window' },
              ]}
            />
          </div>
        )}
        {lhsIsBooleanWindow ? (
          <div className="flex items-center justify-end gap-2">
            <div className="text-xs text-textSecondary">is</div>
            <div className="inline-flex items-center p-0.5 bg-muted rounded-md">
              {[
                { label: 'True', val: 1 },
                { label: 'False', val: 0 },
              ].map((o) => (
                <button
                  key={o.val}
                  type="button"
                  onClick={() => onUpdate((prev)=> ({ ...(prev||normalized), op: 'equals', rhs: o.val }))}
                  className={`px-2 py-0.5 text-xs rounded ${Number(normalized.rhs) === o.val ? 'bg-backgroundSurface text-textPrimary shadow-sm' : 'text-textSecondary'}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        ) : rhsKind === 'indicator' ? (
          <IndicatorCard
            value={
              typeof normalized.rhs === 'object' && normalized.rhs?.type === 'indicator'
                ? normalized.rhs
                : { type: 'indicator', name: 'ema', params: { length: 50 }, input: { type: 'price', source: 'close' } }
            }
            onChange={(next) => onUpdate((prev) => {
              const base = prev || normalized;
              if (import.meta.env?.DEV) console.log('[Clause] rhsIndicatorChange', { before: base?.rhs, next });
              return { ...base, rhs: next };
            })}
            baseTimeframe={baseTimeframe}
          />
        ) : rhsKind === 'math' ? (
          <MathNode
            value={typeof normalized.rhs === 'object' ? normalized.rhs : { op:'SUB', lhs:{ type:'price', source:'close' }, rhs:0 }}
            onChange={(next)=>onUpdate((prev)=> ({ ...(prev||normalized), rhs: next }))}
            baseTimeframe={baseTimeframe}
          />
        ) : rhsKind === 'window' ? (
          <WindowPredicateBuilder
            value={normalized.rhs && normalized.rhs.kind === 'window_pred' ? normalized.rhs : { kind:'window_pred', op:'ANY_TRUE_LAST_N', inputRef:'', n:3 }}
            onChange={(v)=>onUpdate((prev)=>({ ...(prev||normalized), rhs: v }))}
            baseTimeframe={baseTimeframe}
          />
        ) : (
          <Input
            className="h-10 w-full text-center"
            type="number"
            value={typeof normalized.rhs === 'number' ? normalized.rhs : 0}
            onChange={(e) => onUpdate((prev) => {
              const base = prev || normalized;
              const val = parseFloat(e.target.value || '0');
              if (import.meta.env?.DEV) console.log('[Clause] rhsNumberChange', { before: base?.rhs, to: val });
              return { ...base, rhs: val };
            })}
          />
        )}
        {/* RHS timeframe for indicator or price RHS */}
  {!isLhsWindow && rhsKind === 'indicator' && (
          <div className="mt-1 flex items-center justify-end gap-2">
            <select
              className="h-7 px-2 rounded border border-borderDefault bg-backgroundSurface text-textPrimary text-xs"
              value={(typeof normalized.rhs === 'object' && normalized.rhs?.timeframe) || ''}
              onChange={(e) => onUpdate((prev) => {
                const base = prev || normalized;
                const cur = (typeof base.rhs === 'object' && base.rhs) || { type: 'indicator', name: 'ema', params: { length: 50 }, input: { type: 'price', source: 'close' } };
                return { ...base, rhs: { ...cur, timeframe: e.target.value || undefined } };
              })}
            >
              <option value="">Base ({normalized?.timeframe || baseTimeframe})</option>
              {TIMEFRAMES.map(tf => <option key={tf} value={tf}>{tf}</option>)}
            </select>
            {(typeof normalized.rhs === 'object' && normalized.rhs?.timeframe) && (
              <GuardrailHint tf={normalized.rhs.timeframe} base={normalized?.timeframe || baseTimeframe} />
            )}
            {(typeof normalized.rhs === 'object' && normalized.rhs?.timeframe && normalized.rhs.timeframe !== (normalized?.timeframe || baseTimeframe)) && (
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-muted text-textSecondary">TF: {normalized.rhs.timeframe}</span>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col items-center justify-center gap-2">
        <button
          type="button"
          title="Duplicate"
          onClick={() => {
            if (onDuplicate) return onDuplicate();
            // Fallback: shallow update to trigger change (no insert)
            return onUpdate((prev) => ({ ...(prev || normalized) }));
          }}
          className="h-8 w-8 flex items-center justify-center rounded-md border border-transparent hover:bg-muted/60 text-textSecondary"
        >
          <Copy size={14} />
        </button>
        <button
          type="button"
          title="Remove"
          onClick={onRemove}
          className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-red-50/80 text-red-600"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

export default Clause;

// Small guardrail text helper
const GuardrailHint = ({ tf, base }) => {
  const lower = isTfLower(tf, base);
  const clean = isCleanMultiple(tf, base);
  if (!lower && clean) return null;
  const msg = lower
    ? `Warning: ${tf} is lower than base ${base}`
    : `Warning: ${tf} is not a clean multiple of base ${base}`;
  const tip = 'Aligned by last closed bar of ' + tf + '.';
  return (
    <span className="text-[11px] text-amber-600" title={tip + ' ' + msg}>{msg}</span>
  );
};
